import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

/**
 * Configure AWS Amplify with Cognito settings for the admin SPA.
 * Admin users authenticate with email (staff accounts), not phone like the
 * customer-facing racing-react SPA. There is no GraphQL/API block here —
 * admin-api is a plain REST service, configured separately in src/api/client.ts.
 * Call once from the app entry point (src/main.tsx) before rendering.
 *
 * Uses VITE_ADMIN_COGNITO_* (not VITE_COGNITO_*) because racing-react's
 * deploy-frontend-tippingiq/-platinumiq and this SPA's deploy-admin-react-*
 * share the same Bitbucket deployment environment - every VITE_ var on that
 * environment lands in whichever app is building, so reusing racing-react's
 * VITE_COGNITO_USER_POOL_ID/CLIENT_ID (the customer pool) would silently
 * configure this admin app against the wrong Cognito pool.
 */
export function configureAmplify() {
  const userPoolId = import.meta.env.VITE_ADMIN_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_ADMIN_COGNITO_CLIENT_ID;
  const region = import.meta.env.VITE_AWS_REGION || 'ap-southeast-2';

  if (!userPoolId || !clientId) {
    console.warn('Cognito is not configured. Authentication will not work.');
    return;
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId: clientId,
        loginWith: {
          email: true,
          phone: false,
          username: false,
        },
      },
    },
  });

  // Amplify infers the token endpoint region from the pool ID prefix; region is
  // logged here (not passed to Auth.Cognito) purely so misconfiguration is visible.
  console.info(`Amplify configured: pool ${userPoolId} (${region})`);

  cognitoUserPoolsTokenProvider.setKeyValueStorage({
    getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
    setItem: (key: string, value: string) => {
      localStorage.setItem(key, value);
      return Promise.resolve();
    },
    removeItem: (key: string) => {
      localStorage.removeItem(key);
      return Promise.resolve();
    },
    clear: () => {
      localStorage.clear();
      return Promise.resolve();
    },
  });
}
